import { loadData } from "../bridge/loadData";
import { saveData } from "../bridge/saveData";

export interface PersistentDataTypes {
  firstName: string | undefined;
  lastName: string | undefined;
  phoneNumber: string | undefined;
  verificationId: string | undefined;

  freetrial: boolean;

  backgroundMusicEnabled: boolean;
  soundEffectsEnabled: boolean;
  vibrationEnabled: boolean;

  [key: string]: unknown;
}

let cache: Map<keyof PersistentDataTypes, any> | undefined;

async function loadPersistentData() {
  const data = (await loadData()) ?? {};

  cache = new Map(Object.entries(data));
}

export class PersistentData {
  static eventTarget = new EventTarget();

  static addOnChangeListener(listener: (event: Event) => void): () => void {
    PersistentData.eventTarget.addEventListener("change", listener);

    return () => {
      PersistentData.eventTarget.removeEventListener("change", listener);
    };
  }

  static async get<K extends keyof PersistentDataTypes>(
    key: K
  ): Promise<PersistentDataTypes[K] | undefined> {
    if (!cache) {
      await loadPersistentData();
    }

    return cache!.get(key);
  }

  static async getMultiple<K extends keyof PersistentDataTypes, T extends K[]>(
    keys: T
  ): Promise<Partial<Pick<PersistentDataTypes, T[number]>>> {
    if (!cache) {
      await loadPersistentData();
    }

    const result: Record<keyof PersistentDataTypes, any> = {};

    for (const key of keys) {
      result[key] = cache!.get(key);
    }

    return result as Pick<PersistentDataTypes, T[number]>;
  }

  static async set<K extends keyof PersistentDataTypes>(
    key: K,
    value: PersistentDataTypes[K]
  ) {
    if (!cache) {
      await loadPersistentData();
    }

    cache!.set(key, value);

    await saveData(Object.fromEntries(cache!));

    PersistentData.eventTarget.dispatchEvent(new Event("change"));
  }

  static async setMultiple(data: Partial<PersistentDataTypes>) {
    if (!cache) {
      await loadPersistentData();
    }

    for (const [key, value] of Object.entries(data)) {
      cache!.set(key, value);
    }

    await saveData(Object.fromEntries(cache!));

    PersistentData.eventTarget.dispatchEvent(new Event("change"));
  }

  static async clear() {
    cache = new Map();

    await saveData({});

    PersistentData.eventTarget.dispatchEvent(new Event("change"));
  }
}
